import { CollectionSiteSchema } from '../types/collection-site.type';

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const SAUDI_PHONE_REGEX = /^(\+9665|9665|05)[0-9]{8}$/;

export const ROLE_FILTER_OPTIONS = [
  'Admin',
  'Operations manager',
  'Staff',
  'Read-only',
  'Custom',
  'Unknown',
] as const;

export const EMPLOYEE_LEVEL_OPTIONS = ['Senior', 'Mid', 'Line'] as const;
export const GENDER_OPTIONS = ['Male', 'Female'] as const;

export const NATIONALITY_OPTIONS = [
  'Saudi Arabia', 'Egypt', 'India', 'Pakistan', 'Bangladesh', 'Philippines',
  'Indonesia', 'Jordan', 'Lebanon', 'Syria', 'Yemen', 'Sudan', 'Morocco',
  'Tunisia', 'Algeria', 'Iraq', 'Kuwait', 'UAE', 'Bahrain', 'Qatar', 'Oman',
  'Palestine', 'Turkey', 'Afghanistan', 'Sri Lanka', 'Nepal', 'Other',
];

export const formDataDefaults = {
  username: '',
  password: '',
  password_confirm: '',
  phone: '' as string | null,
  first_name: '' as string | null,
  last_name: '' as string | null,
  gender: '' as string | null,
  nationality: '' as string | null,
  date_of_birth: '' as string | null,
  status: 'active' as 'active' | 'inactive',
  employee_level: '' as string | null,
  iban: '' as string | null,
  driver_license_expiry: '' as string | null,
  driver_license_number: '' as string | null,
  vehicle_registration_number: '' as string | null,
};

export type UserFormData = typeof formDataDefaults;

export function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value.trim());
}

export function isValidSaudiPhone(value: string): boolean {
  const normalized = value.trim().replace(/\s/g, '');
  return SAUDI_PHONE_REGEX.test(normalized);
}

export function collectionSiteLabel(site: CollectionSiteSchema): string {
  const d = site.data;
  const name = d?.name ?? d?.title;
  if (typeof name === 'string' && name.trim()) return name.trim();
  if (site.google_maps_url) return site.google_maps_url;
  return site.id.length > 12 ? `${site.id.slice(0, 8)}…` : site.id;
}

export function applySitesEntryToBranchState(
  raw: string[] | undefined
): { all: boolean; ids: string[] } {
  if (!raw?.length) return { all: false, ids: [] };
  if (raw.includes('*')) return { all: true, ids: [] };
  return { all: false, ids: [...raw] };
}

const SITES_LIST_MAX_SHOWN = 5;

export function buildSiteIdToLabelMap(
  sites: CollectionSiteSchema[]
): Record<string, string> {
  const idToLabel: Record<string, string> = {};
  for (const s of sites) {
    idToLabel[s.id] = collectionSiteLabel(s);
  }
  return idToLabel;
}

export function formatSitesListCell(
  sitesEntry: string[] | null | undefined,
  idToLabel: Record<string, string>
): { display: string; full: string } {
  if (sitesEntry == null || sitesEntry.length === 0) {
    return { display: '---', full: '---' };
  }
  if (sitesEntry.includes('*')) {
    return { display: 'All Sites', full: 'All Sites' };
  }
  const names = sitesEntry.map(
    (id) => idToLabel[id] ?? (id.length > 10 ? `${id.slice(0, 8)}…` : id)
  );
  const full = names.join(', ');
  if (names.length <= SITES_LIST_MAX_SHOWN) {
    return { display: full, full };
  }
  const display = `${names.slice(0, SITES_LIST_MAX_SHOWN).join(', ')} (+${names.length - SITES_LIST_MAX_SHOWN} more)`;
  return { display, full };
}
